import { NUMBER_FORMAT_PERCENTAGE, Operator } from '@ardoq/api-types';
import { getOperatorsLabel } from '@ardoq/query-builder';
import { formatNumber } from '@ardoq/locale';
import type {
  DashboardFilterOperatorButtonProps,
  DashboardFilterPillValueProps,
} from '@ardoq/table-filter-bar';
import type { NumberDashboardFilter } from '../../types';
import { toValuePopoverContentProps } from '../toValuePopoverContentProps';
import { type GetOperatorAndValuePropsParams, getValueProps } from './utils';

const numberOperators: Operator[] = [
  Operator.EQUAL,
  Operator.NOT_EQUAL,
  Operator.GREATER,
  Operator.LESS,
  Operator.IS_EMPTY,
  Operator.IS_NOT_EMPTY,
];

export const getNumberFilterOperatorAndValueProps = ({
  filterPillUIState,
  dashboardFilter,
  updateDashboardFilterValues,
  hasInaccessibleWidgets,
  locale,
  i18nLocale,
}: GetOperatorAndValuePropsParams & {
  dashboardFilter: NumberDashboardFilter;
}): {
  operatorProps: DashboardFilterOperatorButtonProps;
  valueProps: DashboardFilterPillValueProps;
} => {
  const operatorUIStateProps = {
    button: {
      isPopoverOpen: filterPillUIState.isOperatorPopoverOpen,
      onClick: filterPillUIState.toggleIsOperatorPopoverOpen,
    },
    popover: {
      isOpen: filterPillUIState.isOperatorPopoverOpen,
      onClose: filterPillUIState.toggleIsOperatorPopoverOpen,
    },
  };

  const filterType = dashboardFilter.type;
  const selectedValue = dashboardFilter.value;
  if (!(selectedValue === null || typeof selectedValue === 'number')) {
    throw new Error(
      `Invalid value type for number filter, value:${selectedValue}, type: ${typeof selectedValue}`
    );
  }

  const isPercentage =
    dashboardFilter.numberFormatOptions === NUMBER_FORMAT_PERCENTAGE;

  // For percentage fields, convert stored fraction (0.5) to display value (50) for editing
  const displayValue =
    isPercentage && selectedValue !== null
      ? selectedValue * 100
      : selectedValue;

  const operatorProps = {
    button: {
      label: getOperatorsLabel(dashboardFilter.operator, i18nLocale),
      ...operatorUIStateProps.button,
    },
    popover: {
      ...operatorUIStateProps.popover,
      options: numberOperators.map(operator => ({
        value: operator,
        label: getOperatorsLabel(operator, i18nLocale),
        onClick: () => {
          const value =
            operator === Operator.IS_EMPTY || operator === Operator.IS_NOT_EMPTY
              ? null
              : (selectedValue ?? 0);
          filterPillUIState.toggleIsOperatorPopoverOpen();
          if (
            operator === Operator.IS_EMPTY ||
            operator === Operator.IS_NOT_EMPTY
          ) {
            filterPillUIState.setPopoverInputValue(0); // reset input value when operator doesn't require a value
          }
          updateDashboardFilterValues(dashboardFilter.id, filterType, [
            {
              operator,
              value,
            },
          ]);
        },
      })),
    },
  };

  const onValueChange = (value: number) => {
    // For percentage fields, convert display value (50) back to stored fraction (0.5)
    const storedValue = isPercentage ? value / 100 : value;
    updateDashboardFilterValues(dashboardFilter.id, filterType, [
      {
        value: storedValue,
        operator: dashboardFilter.operator ?? Operator.EQUAL,
      },
    ]);
  };

  const valueProps = getValueProps({
    onClick: filterPillUIState.toggleIsValuePopoverOpen,
    filterPillUIState,
    name: dashboardFilter.name,
    i18nLocale,
    labelsForSelectedValues:
      selectedValue === null
        ? []
        : [
            formatNumber(
              selectedValue,
              dashboardFilter.numberFormatOptions ?? undefined,
              locale
            ),
          ],
    content: toValuePopoverContentProps(
      {
        type: 'number',
        value: displayValue,
        label: dashboardFilter.name,
        updateDashboardFilterValue: onValueChange,
      },
      filterPillUIState,
      i18nLocale
    ),
    hasLinkedWidgets: dashboardFilter.linkCounts.total > 0,
    isInvalid: false, // TODO: fixme
    hasInaccessibleWidgets,
    clearFilter: () =>
      updateDashboardFilterValues(dashboardFilter.id, filterType, []),
    onClose: filterPillUIState.toggleIsValuePopoverOpen,
  });
  return { operatorProps, valueProps };
};
